import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Card, Field, FormGrid } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { Notice, Section, errorText } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';

interface Reader {
  id: string;
  label: string;
  status: 'online' | 'offline' | null;
  device_type: string | null;
  serial_number: string | null;
  simulated: boolean;
}
interface ReadersResponse {
  location: { id: string; name: string };
  stripe_connected: boolean;
  test_mode: boolean;
  needs_address: boolean;
  items: Reader[];
}

const DEVICE: Record<string, string> = {
  stripe_s700: 'Stripe Reader S700',
  bbpos_wisepos_e: 'BBPOS WisePOS E',
  simulated_wisepos_e: 'Simulated reader (test mode)',
};

interface PosDevice {
  id: string;
  number: number;
  name: string | null;
  location: string | null;
  last_user: string | null;
  last_seen_at: string | null;
  revoked_at: string | null;
  orders_today: number;
}

const seen = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'never';

/**
 * iPads registered as a POS (#58). Each got a random POS number the first time it opened New order
 * in the Kitchen app; walk-up orders record it and who was signed in.
 */
function PosDevices() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['pos-devices'],
    queryFn: async () => (await api.get<{ items: PosDevice[] }>('/admin/pos/devices')).data.items,
    refetchInterval: 30_000,
  });
  const [naming, setNaming] = useState<{ id: string; name: string } | null>(null);
  const [removing, setRemoving] = useState<PosDevice | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const rename = useMutation({
    mutationFn: async (v: { id: string; name: string }) =>
      api.patch(`/admin/pos/devices/${v.id}`, { name: v.name.trim() || null }),
    onSuccess: () => {
      setNaming(null);
      void qc.invalidateQueries({ queryKey: ['pos-devices'] });
    },
    onError: (err) => setMsg({ kind: 'error', text: errorText(err) }),
  });
  const active = (list.data ?? []).filter((d) => !d.revoked_at);
  return (
    <Card>
      <strong>POS devices</strong>
      <p style={{ margin: '4px 0 12px', color: 'var(--ink-soft)' }}>
        An iPad becomes a POS the first time someone opens New order in the Kitchen app on it, and
        gets a random POS number. Each walk-up order shows its POS and who took it.
      </p>
      {list.isError && (
        <Notice kind="error">Could not load POS devices. {errorText(list.error, '')}</Notice>
      )}
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {list.isSuccess && active.length === 0 && (
        <p style={{ margin: 0, color: 'var(--ink-soft)' }}>No POS devices yet.</p>
      )}
      <div style={{ display: 'grid' }}>
        {active.map((d, i) => (
          <div
            key={d.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              minHeight: 60,
              borderTop: i ? '1px solid var(--line)' : 0,
              flexWrap: 'wrap',
              padding: '6px 0',
            }}
          >
            <div style={{ flex: 1, minWidth: 220 }}>
              {naming?.id === d.id ? (
                <form
                  style={{ display: 'flex', gap: 8, alignItems: 'end', flexWrap: 'wrap' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    rename.mutate(naming);
                  }}
                >
                  <Field
                    label={`Name for POS ${d.number}`}
                    value={naming.name}
                    maxLength={40}
                    placeholder="Front iPad"
                    autoFocus
                    onChange={(e) => setNaming({ id: d.id, name: e.target.value })}
                  />
                  <Button type="submit" disabled={rename.isPending}>
                    Save
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setNaming(null)}>
                    Cancel
                  </Button>
                </form>
              ) : (
                <>
                  <strong>
                    POS {d.number}
                    {d.name ? ` · ${d.name}` : ''}
                  </strong>
                  <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
                    {[
                      d.location,
                      d.last_user ? `last used by ${d.last_user}` : null,
                      seen(d.last_seen_at),
                      d.orders_today
                        ? `${d.orders_today} ${d.orders_today === 1 ? 'order' : 'orders'} in the last day`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </>
              )}
            </div>
            {naming?.id !== d.id && (
              <>
                <Button variant="ghost" onClick={() => setNaming({ id: d.id, name: d.name ?? '' })}>
                  Name
                </Button>
                <Button variant="ghost" onClick={() => setRemoving(d)}>
                  Remove
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
      <ConfirmDelete
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={`Remove POS ${removing?.number ?? ''}?`}
        description="Use this for a lost or replaced iPad. Its orders keep the POS number. If the iPad opens New order again, it registers with a new number."
        actionLabel="Remove"
        pendingLabel="Removing…"
        permission="pos.readers"
        onConfirm={async () => {
          await api.delete(`/admin/pos/devices/${removing!.id}`);
          void qc.invalidateQueries({ queryKey: ['pos-devices'] });
        }}
      />
    </Card>
  );
}

/** Card readers for the Kitchen app POS, paired per location (#55). */
export function CardReaders() {
  const { can, location } = useStaff();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['pos-readers', location.slug],
    queryFn: async () =>
      (await api.get<ReadersResponse>(`/admin/pos/readers?location=${location.slug}`)).data,
    enabled: can('pos.readers'),
    refetchInterval: 20_000,
  });
  const [label, setLabel] = useState('');
  const [code, setCode] = useState('');
  const [addr, setAddr] = useState({ line1: '', city: '', state: 'FL', postal_code: '' });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [removing, setRemoving] = useState<Reader | null>(null);

  const add = useMutation({
    mutationFn: async () =>
      (
        await api.post<Reader>('/admin/pos/readers', {
          location_id: list.data!.location.id,
          registration_code: code.trim(),
          label: label.trim(),
          ...(list.data!.needs_address ? { address: addr } : {}),
        })
      ).data,
    onSuccess: (r) => {
      setMsg({
        kind: 'ok',
        text: `${r.label} is paired. It shows up in the Kitchen app right away.`,
      });
      setLabel('');
      setCode('');
      void qc.invalidateQueries({ queryKey: ['pos-readers', location.slug] });
    },
    onError: (err) => setMsg({ kind: 'error', text: errorText(err) }),
  });

  if (!can('pos.readers'))
    return (
      <Section title="POS & card readers">
        <Notice>Owners and managers manage POS devices and card readers.</Notice>
      </Section>
    );
  const d = list.data;

  return (
    <Section title="POS & card readers">
      <PosDevices />
      <h2 style={{ margin: '8px 0 0', fontSize: 20 }}>Card readers</h2>
      <p style={{ margin: '-8px 0 0', color: 'var(--ink-soft)' }}>
        Readers take walk-up payments in the Kitchen app under New order.
        {d ? ` These are the readers at ${d.location.name}.` : ''}
      </p>
      {list.isError && (
        <Notice kind="error">Could not load readers. {errorText(list.error, '')}</Notice>
      )}
      {d && !d.stripe_connected && (
        <Notice kind="error">Connect Stripe in Integrations before pairing a reader.</Notice>
      )}

      <Card>
        {d && d.items.length === 0 && (
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>No readers yet.</p>
        )}
        <div style={{ display: 'grid' }}>
          {d?.items.map((r, i) => (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 56,
                borderTop: i ? '1px solid var(--line)' : 0,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ flex: 1, minWidth: 180 }}>
                <strong>{r.label}</strong>
                <div style={{ fontSize: 14, color: 'var(--ink-soft)' }}>
                  {[
                    r.device_type ? (DEVICE[r.device_type] ?? r.device_type) : null,
                    r.serial_number,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Details load when Stripe answers'}
                </div>
              </div>
              <span className="crave-status" data-tone={r.status === 'online' ? 'ok' : 'warn'}>
                {r.status === 'online' ? 'Ready' : r.status === 'offline' ? 'Offline' : 'Unknown'}
              </span>
              <Button variant="ghost" onClick={() => setRemoving(r)}>
                Remove
              </Button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <form
          style={{ display: 'grid', gap: 14 }}
          onSubmit={(e) => {
            e.preventDefault();
            setMsg(null);
            if (!label.trim() || !code.trim()) {
              setMsg({ kind: 'error', text: 'Enter a name and the pairing code from the reader.' });
              return;
            }
            add.mutate();
          }}
        >
          <strong>Pair a reader</strong>
          <ol
            style={{
              margin: 0,
              paddingLeft: 20,
              color: 'var(--ink-soft)',
              display: 'grid',
              gap: 4,
            }}
          >
            <li>Turn the reader on and connect it to Wi-Fi.</li>
            <li>On the reader, open Settings and choose Generate pairing code.</li>
            <li>Type the code here with a name you’ll recognize, like Counter.</li>
          </ol>
          {d?.test_mode && (
            <Notice>
              Stripe is in test mode. Use the code simulated-wpe for a practice reader.
            </Notice>
          )}
          <FormGrid min={220}>
            <Field
              label="Name"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Counter"
            />
            <Field
              label="Pairing code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </FormGrid>
          {d?.needs_address && (
            <>
              <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
                First reader at this location: Stripe needs the address where payments are taken.
              </p>
              <FormGrid min={180}>
                <Field
                  label="Street"
                  value={addr.line1}
                  onChange={(e) => setAddr({ ...addr, line1: e.target.value })}
                  wide
                />
                <Field
                  label="City"
                  value={addr.city}
                  onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                />
                <Field
                  label="State"
                  value={addr.state}
                  maxLength={2}
                  onChange={(e) => setAddr({ ...addr, state: e.target.value.toUpperCase() })}
                />
                <Field
                  label="ZIP"
                  inputMode="numeric"
                  value={addr.postal_code}
                  onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })}
                />
              </FormGrid>
            </>
          )}
          {msg && (
            <div role="status">
              <Notice kind={msg.kind}>{msg.text}</Notice>
            </div>
          )}
          <div>
            <Button type="submit" disabled={!d?.stripe_connected || add.isPending}>
              {add.isPending ? 'Pairing…' : 'Pair reader'}
            </Button>
          </div>
        </form>
      </Card>

      <ConfirmDelete
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        title={`Remove ${removing?.label ?? 'reader'}?`}
        description="It stops taking payments. You can pair it again later with a new code."
        actionLabel="Remove"
        pendingLabel="Removing…"
        permission="pos.readers"
        onConfirm={async () => {
          await api.delete(`/admin/pos/readers/${removing!.id}`);
          void qc.invalidateQueries({ queryKey: ['pos-readers', location.slug] });
        }}
      />
    </Section>
  );
}
